import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Loader2, Plus, Check, Bell } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

const SUBCATEGORY_LABELS = { football_soccer: 'Football', tennis: 'Tennis', cricket: 'Cricket', esports: 'eSports' };
const SUBCATEGORY_IMAGES = {
  football_soccer: 'https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&w=400&h=300&fit=crop',
  tennis: 'https://images.pexels.com/photos/32289802/pexels-photo-32289802.jpeg?auto=compress&w=400&h=300&fit=crop',
  cricket: 'https://images.unsplash.com/photo-1730739463889-34c7279277a9?w=400&h=300&fit=crop',
  esports: 'https://images.pexels.com/photos/9072293/pexels-photo-9072293.jpeg?auto=compress&w=400&h=300&fit=crop',
};

const MarketCard = ({ market, onMarketClick, onYesClick, onNoClick }) => {
  const { isAuthenticated } = useAuth();
  const { addMarketToWatch, removeMarketFromWatch, isEnabled: notificationsEnabled } = useNotifications();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [showNotifHint, setShowNotifHint] = useState(false);

  // Convert backend format to display format
  // Calculate prices - ensure YES + NO = 100
  const rawYesPrice = market.yes_price || (market.prices?.YES_price_bp ? Math.round(market.prices.YES_price_bp / 100) : 50);
  const yesPrice = Math.min(99, Math.max(1, Math.round(rawYesPrice)));
  const noPrice = 100 - yesPrice;  // Always complementary
  
  // Check if market is resolved (past closing date or status is RESOLVED)
  const isResolved = market.status === 'RESOLVED' || 
    (market.closes_at && new Date(market.closes_at) < new Date());
  
  // Check watchlist status on mount
  useEffect(() => {
    if (isAuthenticated && market.id) {
      apiService.checkWatchlist(market.id).then(res => {
        setInWatchlist(res.in_watchlist);
      }).catch(() => {});
    }
  }, [isAuthenticated, market.id]);

  const handleWatchlistToggle = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) return;
    
    setWatchlistLoading(true);
    try {
      if (inWatchlist) {
        await apiService.removeFromWatchlist(market.id);
        setInWatchlist(false);
        removeMarketFromWatch(market.id);
      } else {
        await apiService.addToWatchlist(market.id);
        setInWatchlist(true);
        addMarketToWatch(market.id);
        // Show notification hint if notifications are enabled
        if (notificationsEnabled) {
          setShowNotifHint(true);
          setTimeout(() => setShowNotifHint(false), 3000);
        }
      }
    } catch (error) {
      console.error('Watchlist error:', error);
    } finally {
      setWatchlistLoading(false);
    }
  };

  const handleYesClick = (e) => {
    e.stopPropagation();
    const marketData = {
      ...market,
      options: [
        { name: 'Yes', percentage: yesPrice },
        { name: 'No', percentage: noPrice }
      ],
      selectedOption: 'yes'
    };
    if (onYesClick) {
      onYesClick(marketData);
    } else if (onMarketClick) {
      onMarketClick(marketData);
    }
  };

  const handleNoClick = (e) => {
    e.stopPropagation();
    const marketData = {
      ...market,
      options: [
        { name: 'Yes', percentage: yesPrice },
        { name: 'No', percentage: noPrice }
      ],
      selectedOption: 'no'
    };
    if (onNoClick) {
      onNoClick(marketData);
    } else if (onMarketClick) {
      onMarketClick(marketData);
    }
  };

  const handleCardClick = () => {
    if (onMarketClick) {
      onMarketClick({
        ...market,
        options: [
          { name: 'Yes', percentage: yesPrice },
          { name: 'No', percentage: noPrice }
        ]
      });
    }
  };
  
  return (
    <div 
      onClick={handleCardClick}
      data-testid={`market-card-${market.id}`}
      className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.3)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.4)] hover:border-[#3B82F6]/30 hover:translate-y-[-4px] transition-all duration-300 cursor-pointer group"
    >
      {/* Image Header */}
      <div className="relative h-32 overflow-hidden">
        <img
          src={market.image || SUBCATEGORY_IMAGES[market.subcategory] || `https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop`}
          alt={market.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=300&fit=crop';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050810]/80 to-transparent" />
        
        {/* Live badge */}
        {market.is_live && !isResolved && (
          <div className="absolute top-3 left-3 flex items-center space-x-1.5 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white text-xs font-medium">LIVE</span>
          </div>
        )}
        
        {/* Resolved badge — shows the winning outcome (YES / NO / TIE) */}
        {isResolved && (() => {
          const raw = (market.resolved_outcome || market.winning_outcome || '').toString().toUpperCase();
          const isTie = raw === 'TIE' || raw === 'DRAW' || raw === 'CANCELED' || raw === 'CANCELLED';
          const winner = raw === 'YES' ? 'YES' : raw === 'NO' ? 'NO' : isTie ? 'TIE' : '';
          const bg =
            winner === 'YES' ? 'bg-green-500/90' :
            winner === 'NO' ? 'bg-red-500/90' :
            winner === 'TIE' ? 'bg-zinc-500/90' :
            'bg-amber-500/80';
          return (
            <div
              data-testid={`resolved-badge-${market.id}`}
              className={`absolute top-3 left-3 flex items-center space-x-1.5 ${bg} backdrop-blur-sm px-2 py-1 rounded-full shadow-lg`}
            >
              <span className="text-white text-xs font-bold tracking-wide">
                {winner ? `${winner} WON` : 'RESOLVED'}
              </span>
            </div>
          );
        })()}
        
        {/* Category badge */}
        {market.category && (
          <div className="absolute top-3 right-12 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg">
            <span className="text-white text-xs font-medium">{SUBCATEGORY_LABELS[market.subcategory] || market.category}</span>
          </div>
        )}

        {/* Watchlist button */}
        <button
          onClick={handleWatchlistToggle}
          disabled={watchlistLoading || !isAuthenticated}
          data-testid={`watchlist-btn-${market.id}`}
          className={`absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full transition-all ${
            inWatchlist
              ? 'bg-[#3B82F6] text-white'
              : 'bg-black/60 backdrop-blur-sm text-white hover:bg-[#3B82F6]'
          } ${!isAuthenticated ? 'opacity-50' : ''}`}
          title={isAuthenticated ? (inWatchlist ? 'Remove from watchlist' : 'Add to watchlist') : 'Sign in to add to watchlist'}
        >
          {watchlistLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : inWatchlist ? (
            <Check className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
        </button>

        {/* Notification hint toast */}
        {showNotifHint && (
          <div className="absolute top-12 right-2 bg-[#3B82F6] text-white text-xs px-2 py-1 rounded-lg shadow-lg animate-fade-in flex items-center gap-1">
            <Bell className="h-3 w-3" />
            Price alerts enabled!
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-200 text-sm line-clamp-2 min-h-[40px] group-hover:text-[#3B82F6] transition-colors">
          {market.title}
        </h3>

        {/* Options */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-[#050810] hover:bg-[#1e2a45] transition-colors">
            <span className="text-sm text-gray-400 font-medium">Prediction</span>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-bold text-white">
                {yesPrice}%
              </span>
              <div className="flex items-center space-x-1">
                <button 
                  data-testid={`yes-btn-${market.id}`}
                  onClick={handleYesClick}
                  className="px-2.5 py-1 text-xs font-semibold text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] rounded-md shadow-[0_4px_0_0_#1E40AF,0_6px_10px_rgba(37,99,235,0.3)] hover:shadow-[0_2px_0_0_#1E40AF,0_4px_8px_rgba(37,99,235,0.3)] hover:translate-y-[2px] active:shadow-[0_0px_0_0_#1E40AF] active:translate-y-[4px] transition-all duration-100"
                >
                  Yes {yesPrice}TK¢
                </button>
                <span className="text-gray-600">/</span>
                <button 
                  data-testid={`no-btn-${market.id}`}
                  onClick={handleNoClick}
                  className="px-2.5 py-1 text-xs font-semibold text-white bg-gradient-to-b from-[#FB923C] to-[#EA580C] rounded-md shadow-[0_4px_0_0_#C2410C,0_6px_10px_rgba(249,115,22,0.3)] hover:shadow-[0_2px_0_0_#C2410C,0_4px_8px_rgba(249,115,22,0.3)] hover:translate-y-[2px] active:shadow-[0_0px_0_0_#C2410C] active:translate-y-[4px] transition-all duration-100"
                >
                  No {noPrice}TK¢
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <span className="font-medium">{market.status}</span>
          {market.closes_at && (
            <div className="flex items-center space-x-1">
              <RefreshCw className="h-3 w-3" />
              <span>Closes {new Date(market.closes_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MarketGrid = ({ selectedCategory, closingDateFilter = 'all', onMarketClick }) => {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const requestIdRef = useRef(0);
  const LIMIT = 20;
  const wsRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to filter markets by closing date
  const filterByClosingDate = (marketList) => {
    if (closingDateFilter === 'all') return marketList;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    
    // End of this week (Sunday)
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);
    
    // End of this month
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
    
    // End of next month
    const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999);
    const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

    return marketList.filter(market => {
      if (!market.closes_at) return false;
      
      const closingDate = new Date(market.closes_at);
      
      switch (closingDateFilter) {
        case 'today':
          return closingDate >= today && closingDate < tomorrow;
        case 'tomorrow':
          return closingDate >= tomorrow && closingDate < dayAfterTomorrow;
        case 'this-week':
          return closingDate >= today && closingDate <= endOfWeek;
        case 'this-month':
          return closingDate >= today && closingDate <= endOfMonth;
        case 'next-month':
          return closingDate >= startOfNextMonth && closingDate <= endOfNextMonth;
        default:
          return true;
      }
    });
  };

  // WebSocket connection for real-time price updates
  useEffect(() => {
    const connectWs = () => {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws`); // Global feed
      
      ws.onopen = () => {
        console.log('MarketGrid WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          if (data.type === 'price_update' && data.market_id && data.prices) {
            console.log('Price update for market:', data.market_id, 'prices:', data.prices);
            // Update the market in our list with new prices
            setMarkets(prevMarkets => {
              const updated = prevMarkets.map(market => {
                if (market.id === data.market_id) {
                  console.log('Updating market', market.id, 'from', market.yes_price, 'to', data.prices.yes_price);
                  return {
                    ...market,
                    yes_price: data.prices.yes_price,
                    no_price: data.prices.no_price,
                    last_trade_price: data.prices.last_trade_price,
                    last_trade_outcome: data.prices.last_trade_outcome
                  };
                }
                return market;
              });
              return updated;
            });
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
      
      ws.onerror = (error) => {
        console.error('MarketGrid WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('MarketGrid WebSocket disconnected, reconnecting...');
        setTimeout(connectWs, 3000);
      };
      
      wsRef.current = ws;
    };
    
    connectWs();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [API_URL]);

  const fetchMarkets = async (cursor = null, append = false) => {
    const requestId = ++requestIdRef.current;
    try {
      if (!cursor) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      let status = 'OPEN';
      let category = null;
      let sort = null;
      
      // Handle resolved category - fetch markets with RESOLVED status
      if (selectedCategory === 'resolved') {
        status = 'RESOLVED';
        category = null;
      } else if (selectedCategory === 'trending') {
        // Trending shows recently traded markets
        sort = 'activity';
      } else if (selectedCategory !== 'all' && selectedCategory !== 'new') {
        category = selectedCategory;
      }
      
      const data = await apiService.getMarkets(status, category, null, LIMIT, cursor, sort);
      if (requestId !== requestIdRef.current) return; // a newer request superseded this one
      const newMarkets = data.markets || [];
      
      if (append) {
        setMarkets(prev => [...prev, ...newMarkets]);
      } else {
        setMarkets(newMarkets);
      }
      
      setNextCursor(data.next_cursor || null);
      setHasMore(data.has_more || false);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch markets:', err);
      if (requestId === requestIdRef.current) setError('Failed to load markets');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    setNextCursor(null);
    setHasMore(true);
    fetchMarkets(null, false);
  }, [selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (nextCursor) {
      fetchMarkets(nextCursor, true);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading markets...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center text-red-400">{error}</div>
      </div>
    );
  }

  // Apply closing date filter to markets
  const filteredMarkets = filterByClosingDate(markets);

  if (markets.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="text-center text-gray-400 mb-8 py-8">
          No markets found for this category
        </div>
      </div>
    );
  }

  if (filteredMarkets.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="text-center text-gray-400 mb-8 py-8">
          No markets found with the selected closing date filter
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredMarkets.map((market) => (
          <MarketCard 
            key={market.id} 
            market={market} 
            onMarketClick={onMarketClick}
            onYesClick={onMarketClick}
            onNoClick={onMarketClick}
          />
        ))}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center mt-8">
          <button 
            onClick={handleLoadMore}
            disabled={loadingMore}
            data-testid="load-more-markets"
            className="px-8 py-3 text-sm font-semibold text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] rounded-full shadow-[0_6px_0_0_#1E40AF,0_8px_20px_rgba(37,99,235,0.3)] hover:shadow-[0_3px_0_0_#1E40AF,0_6px_15px_rgba(37,99,235,0.3)] hover:translate-y-[3px] active:shadow-[0_0px_0_0_#1E40AF] active:translate-y-[6px] transition-all duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingMore ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </span>
            ) : (
              'Load more markets'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketGrid;
